import { cloneElement, memo, forwardRef, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { combineRefs, makeStyles } from 'hooks';
import EmptyFiller from 'components/emptyFiller';
import Spinner from 'components/spinner';
import api, { NotFoundError } from 'services/api';
import validator from 'validator';

import { VideoOutline } from 'mdi-material-ui';

const validVideoDomains = [
  'www.youtube.com',
  'player.vimeo.com',
  'fast.wistia.net',
  'www.loom.com',
  'cdn.jwplayer.com',
  'players.brightcove.net',
  'embed.app.guidde.com',
  'go.screenpal.com'
];

export function transformEmbedUrl(url) {
  // Transform default youtube link to embed url
  const yt = /https:\/\/youtu.be\/(.*?)(?:\?.*|$)/.exec(url);
  if (yt) {
    return 'https://www.youtube.com/embed/' + yt[1];
  }

  // Transform default vimeo
  const vm = /https:\/\/vimeo.com\/(.*?)(?:\?.*|$)/.exec(url);
  if (vm) {
    return 'https://player.vimeo.com/video/' + vm[1];
  }

  return url;
};

export function isValidEmbed(url) {
  if (!validator.isURL(url, {
    protocols: [ 'https' ],
    require_protocol: true
  })) { return 'Not a valid url, must include \'https\''; }

  const domain = (new URL(transformEmbedUrl(url))).hostname;
  return validVideoDomains.includes(domain) ? null : 'This embed url is not from a valid video site, please contact Kalix Support for help';
}

const useFrameStyles = makeStyles(theme =>
  ({
    outerDiv: {
      position: 'relative',
      paddingTop: (9 * 100 / 16) + '%'
    },
    frame: {
      border: 'none',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      display: 'flex'
    }
  })
);

export const VideoFrame = memo(forwardRef(function VideoFrame({ videoIdUrl, embedUrl, ...other }, ref) {
  const classes = useFrameStyles();
  const [ notFound, setNotFound ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(true);

  const hasVideo = !(!videoIdUrl && !embedUrl) && !notFound;

  return <div className={classes.outerDiv}>
    { hasVideo && isLoading && <div className={classes.frame}><Spinner text="Loading..." /></div> }
    { !hasVideo && <div className={classes.frame}><EmptyFiller icon={<VideoOutline />} header="Video Not Found" center /></div> }
    { !!videoIdUrl && <CloudflarePlayer ref={ref} videoIdUrl={videoIdUrl} setNotFound={setNotFound} setIsLoading={setIsLoading} {...other} /> }
    { !videoIdUrl && !!embedUrl && <EmbedPlayer ref={ref} src={embedUrl} setNotFound={setNotFound} setIsLoading={setIsLoading} title="Video Player" {...other} /> }
  </div>;
}));

const EmbedPlayer = memo(forwardRef(function EmbedPlayer({ src, setNotFound, setIsLoading, forceValid, ...other }, ref) {
  const classes = useFrameStyles();
  const isValid = !!forceValid || !isValidEmbed(src);

  useEffect(() => {
    setNotFound && setNotFound(!isValid);
    setIsLoading && setIsLoading(false);
  }, [ setNotFound, setIsLoading, isValid ]);

  if (!isValid) { return null; }

  return <iframe
    ref={ref}
    { ...other }
    className={classes.frame}
    src={`/iframe?src=${encodeURIComponent(transformEmbedUrl(src))}`}
    frameBorder="0"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    referrerPolicy="strict-origin-when-cross-origin"
    allowFullScreen
  />;
}));

const CloudflarePlayer = memo(forwardRef(function CloudflarePlayer({ videoIdUrl, setNotFound, setIsLoading, ...other }, ref) {
  const [ src, setSrc ] = useState(null);

  useEffect(() => {
    setSrc(null);
    setNotFound(false);
    if (!videoIdUrl) { setIsLoading(false); return; }

    setIsLoading(true);
    const promise = api.get(videoIdUrl)
      .then(res => setSrc(res.data.url))
      .catch(NotFoundError, () => {
        setNotFound(true);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));

    return () => promise.cancel();
  }, [ videoIdUrl, setNotFound, setIsLoading ]);

  return src ? <EmbedPlayer title="Kalix Video Player" ref={ref} {...other} src={src} forceValid /> : null;
}));

export default memo(forwardRef(function Video({ fallbackSrc, src, fallbackComponent, file, onLoad, onFileLoaded, onDurationChange, cacheBust, ...other }, ref) {
  const [ hasFileError, setHasFileError ] = useState(false);
  const [ hasSrcError, setHasSrcError ] = useState(false);

  const [ fileSrc, fileFallback ] = useMemo(() => {
    let fileLink = null;
    let fileFallback = null;
    if (Array.isArray(file)) {
      const [ isFile, path, fallback ] = file;
      fileLink = isFile ? api.openFileUrl(path) : path;
      fileFallback = fallback;
    } else if (file) {
      fileLink = api.openFileUrl(file);
    }
    return [ fileLink || null, fileFallback || null ];
  }, [ file ]);

  const handleError = useCallback(() => {
    if (fileSrc && !hasFileError) {
      setHasFileError(true);
    } else {
      setHasSrcError(true);
    }
  }, [ fileSrc, hasFileError ]);

  const handleOnLoad = useCallback(e => {
    onLoad && onLoad(e);
    if (fileSrc && !hasFileError) {
      onFileLoaded && onFileLoaded(true);
    }
  }, [ onLoad, onFileLoaded, fileSrc, hasFileError ]);

  const compareSrc = fileSrc || src;
  useEffect(() => {
    setHasFileError(false);
    setHasSrcError(false);
    onFileLoaded && onFileLoaded(false);
  }, [ compareSrc, cacheBust, onFileLoaded ]);

  let finalSrc = !fileSrc || hasFileError ? src : fileSrc;
  if (hasSrcError || !finalSrc) {
    finalSrc = fallbackComponent ? null : fileFallback || fallbackSrc;
  }

  const refreshRef = useRef(null);
  useEffect(() => {
    if (!finalSrc || !cacheBust) { return; }
    refreshRef.current.src = finalSrc + (finalSrc.includes('?') ? '&' : '?') + new Date().getTime();
  }, [ cacheBust, finalSrc, handleError ]);

  const inputRef = combineRefs(ref, refreshRef);

  const handleDurationChange = useCallback(() => {
    onDurationChange && refreshRef.current && onDurationChange(refreshRef.current.duration);
  }, [ onDurationChange ]);

  return !finalSrc && fallbackComponent
    ? (typeof fallbackComponent === 'string' ? fallbackComponent : cloneElement(fallbackComponent, { ref: inputRef })) // Passthrough ref to fallback component
    : <video loop autoPlay ref={inputRef} src={finalSrc} onError={handleError} onLoadedData={handleOnLoad} onDurationChange={handleDurationChange} {...other} />;
}));